import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useI18n } from '../../contexts/I18nContext';
import { getInitials, humanize } from '../../utils/helpers';
import GlobalSearch from '../common/GlobalSearch';
import LanguageSwitcher from '../common/LanguageSwitcher';

/* ─── Phase badge styling ───────────────────────────────────────── */
const PHASE_COLORS = {
  construction: { bg: '#1E6FBA', text: '#fff',  label: 'Construction' },
  handover:     { bg: '#F09A5A', text: '#000',  label: 'Handover' },
  community:    { bg: '#28A745', text: '#fff',  label: 'Community' },
};

const Topbar = ({ collapsed, onToggleSidebar, onToggleMobile }) => {
  const { user, logout } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const location = useLocation();
  const { project } = useProject();
  const { unreadCount } = useNotifications();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isInsideProject = Boolean(projectId && location.pathname.includes(`/projects/${projectId}`));

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
    navigate('/login');
  };

  const phaseInfo = project ? PHASE_COLORS[project.phase] || PHASE_COLORS.construction : null;

  return (
    <header className={`topbar ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="topbar-left">
        {/* Desktop toggle */}
        <button className="topbar-toggle d-none d-lg-block" onClick={onToggleSidebar}>
          <i className={`bi ${collapsed ? 'bi-list' : 'bi-text-indent-left'}`}></i>
        </button>
        {/* Mobile toggle */}
        <button className="topbar-toggle d-lg-none" onClick={onToggleMobile}>
          <i className="bi bi-list"></i>
        </button>

        {/* App name */}
        <span className="fw-bold d-none d-md-inline" style={{ fontSize: '1.1rem', color: '#1B3A5C', letterSpacing: '-0.02em' }}>
          Project<span style={{ color: '#1E6FBA' }}>Rax</span>
        </span>

        {/* Project context indicator */}
        {isInsideProject && project && (
          <div className="d-none d-md-flex align-items-center gap-2 ms-3 ps-3" style={{ borderLeft: '1px solid #e2e8f0' }}>
            <span className="text-truncate" style={{ maxWidth: '200px', fontSize: '0.875rem', fontWeight: 600, color: '#2D3748' }}>
              {project.name}
            </span>
            {phaseInfo && (
              <span
                className="badge"
                style={{
                  backgroundColor: phaseInfo.bg,
                  color: phaseInfo.text,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '0.25em 0.6em',
                }}
              >
                {phaseInfo.label}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Global Search */}
      <div className="d-none d-md-flex flex-grow-1 justify-content-center px-3">
        <GlobalSearch />
      </div>

      <div className="topbar-right">
        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Theme Toggle */}
        <button className="topbar-toggle" onClick={toggleTheme} title={darkMode ? 'Light mode' : 'Dark mode'}>
          <i className={`bi ${darkMode ? 'bi-sun-fill' : 'bi-moon-fill'}`}></i>
        </button>

        {/* Notifications */}
        <button
          className="topbar-toggle position-relative"
          onClick={() => navigate('/app/notifications')}
          title="Notifications"
        >
          <i className="bi bi-bell"></i>
          {unreadCount > 0 && (
            <span
              className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
              style={{ fontSize: '0.6rem', padding: '0.2rem 0.4rem', transform: 'translate(-60%, 20%)' }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* User dropdown */}
        <div className="position-relative" ref={dropdownRef}>
          <div className="topbar-user" onClick={() => setDropdownOpen(!dropdownOpen)}>
            <div className="topbar-avatar">{getInitials(user?.name)}</div>
            <div className="topbar-user-info d-none d-md-block">
              <div className="user-name">{user?.name}</div>
              <div className="user-role">{humanize(user?.role)}</div>
            </div>
            <i className="bi bi-chevron-down" style={{ fontSize: '0.7rem', color: '#94a3b8' }}></i>
          </div>

          {dropdownOpen && (
            <div className="dropdown-menu show position-absolute end-0 mt-1" style={{ minWidth: '200px' }}>
              <div className="px-3 py-2 border-bottom">
                <div className="fw-bold" style={{ fontSize: '0.875rem' }}>{user?.name}</div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>{user?.email}</div>
              </div>
              <button
                className="dropdown-item py-2"
                onClick={() => { setDropdownOpen(false); navigate('/app/profile'); }}
              >
                <i className="bi bi-person me-2"></i> {t('tb_profile')}
              </button>
              <button
                className="dropdown-item py-2"
                onClick={() => { setDropdownOpen(false); navigate('/app/change-password'); }}
              >
                <i className="bi bi-key me-2"></i> {t('tb_changePassword')}
              </button>
              <div className="dropdown-divider"></div>
              <button className="dropdown-item py-2 text-danger" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right me-2"></i> {t('tb_logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
